<template>
  <div class="col-12">
    <div class="mb-4 ps-3 no-print d-flex justify-content-between align-items-center">
      <h5 class="mb-0">{{ t('schedule.children_schedule_title') }}</h5>
      <button class="btn btn-primary btn-sm" @click="printSchedule">
        <i class="fas fa-print ms-2"></i>
        {{ t('schedule.print') }}
      </button>
    </div>

    <LoadingSpinner v-if="loading" :message="t('schedule.load_failed')" class="py-3 py-5" />

    <div v-else-if="children.length === 0" class="text-center text-muted py-5">
      <i class="fas fa-calendar-times fa-2x mb-2"></i>
      <p>{{ t('schedule.no_children') }}</p>
    </div>

    <!-- One card per child, so a guardian with more than one child sees
         every child's own weekly schedule, clearly labelled. -->
    <div v-else id="schedule-to-print">
      <div
        v-for="child in children"
        :key="child.student.id"
        class="card mb-5"
        style="margin-top: 2rem; overflow: visible"
      >
        <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px;">
          <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
              {{ child.student.name }}
              <small v-if="child.class" class="opacity-75">
                - {{ child.class.name }} - {{ child.group?.name }}
              </small>
            </h6>
          </div>
        </div>

        <div class="card-body px-0 pb-2 pe-3">
          <div v-if="!child.class" class="text-center text-muted py-4">
            {{ t('schedule.student_not_assigned') }}
          </div>

          <div v-else class="table-responsive p-4">
            <!-- Mobile View -->
            <div class="d-block d-md-none">
              <div v-for="day in days" :key="'mobile-'+child.student.id+'-'+day" class="mb-4">
                <h5 class="fw-bold text-center mb-3">{{ t(`${day}`) }}</h5>
                <div class="list-group">
                  <div
                    v-for="period in getPeriodsForDayIn(child.schedules, day)"
                    :key="'mobile-'+child.student.id+'-'+day+'-'+period.value"
                    class="list-group-item"
                    :class="{
                      'list-group-item-primary': hasScheduleIn(child.schedules, day, period.backendValue),
                      'list-group-item-secondary': period.value === 'استراحة'
                    }"
                  >
                    <div class="d-flex justify-content-between">
                      <span>{{ period.label }}</span>
                      <small class="text-muted">{{ period.time }}</small>
                    </div>
                    <div v-if="period.value !== 'استراحة'" class="mt-2">
                      {{ getCellIn(child.schedules, day, period.backendValue)?.subject?.name || '---' }}
                    </div>
                    <div v-else class="mt-2 text-center">
                      {{ t('schedule.break') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Desktop View -->
            <table class="table table-bordered text-center d-none d-md-table">
              <thead>
                <tr>
                  <th class="bg-light">{{ t('schedule.day_period') }}</th>
                  <th
                    v-for="period in periodsWithTimes"
                    :key="period.value"
                    class="bg-light"
                  >
                    {{ period.label }}<br />
                    <small class="text-muted">{{ period.time }}</small>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="day in days" :key="day">
                  <td class="bg-light fw-bold">{{ t(`${day}`) }}</td>

                  <template v-if="day === 'السبت' && !saturdayHasSchedulesIn(child.schedules)">
                    <td
                      :colspan="periodsWithTimes.length"
                      class="empty-day"
                      style="height: 100px; vertical-align: middle;"
                    >
                      {{ t('schedule.no_classes') }}
                    </td>
                  </template>

                  <template v-else>
                    <td
                      v-for="period in getPeriodsForDayIn(child.schedules, day)"
                      :key="period.value"
                      :class="{
                        'text-primary fw-bold': hasScheduleIn(child.schedules, day, period.backendValue),
                        'bg-secondary text-white': period.value === 'استراحة'
                      }"
                    >
                      <template v-if="period.value === 'استراحة'">
                        {{ t('schedule.break') }}
                      </template>
                      <template v-else>
                        {{ getCellIn(child.schedules, day, period.backendValue)?.subject?.name || '' }}
                      </template>
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useToast } from "vue-toastification";
import api from "@/api";
import { useI18n } from "vue-i18n";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";

const { t } = useI18n();
const toast = useToast();

const children = ref([]);
const loading = ref(false);
const scheduleConfig = ref({
  start_time: "08:00",
  period_duration: 45,
  total_periods: 6,
  break_duration: 30,
  break_after_period: 2
});

const days = [
  "السبت",
  "الأحد",
  "الاثنين",
  "الثلاثاء",
  "الأربعاء",
  "الخميس",
];

const periodsWithTimes = computed(() => {
  const periods = [];
  const startTime = new Date(`2000-01-01T${scheduleConfig.value.start_time}`);
  const periodDuration = scheduleConfig.value.period_duration;
  const breakDuration = scheduleConfig.value.break_duration;
  const totalPeriods = scheduleConfig.value.total_periods;
  const breakAfterPeriod = scheduleConfig.value.break_after_period;

  let currentTime = new Date(startTime.getTime());

  for (let i = 1; i <= totalPeriods; i++) {
    const periodEnd = new Date(currentTime.getTime() + periodDuration * 60000);

    const periodName = getPeriodName(i);
    const backendPeriodName = getBackendPeriodName(i);

    periods.push({
      value: periodName,
      backendValue: backendPeriodName,
      label: `${t('schedule.period')} ${periodName}`,
      time: `${formatTime(currentTime)} - ${formatTime(periodEnd)}`
    });

    if (i === breakAfterPeriod) {
      const breakStart = new Date(periodEnd.getTime());
      const breakEnd = new Date(breakStart.getTime() + breakDuration * 60000);

      periods.push({
        value: "استراحة",
        backendValue: "استراحة",
        label: t('schedule.break'),
        time: `${formatTime(breakStart)} - ${formatTime(breakEnd)}`
      });

      currentTime = new Date(breakEnd.getTime());
    } else {
      currentTime = new Date(periodEnd.getTime());
    }
  }

  return periods;
});

const getPeriodName = (number) => {
  const periodNames = {
    1: t('schedule.first'),
    2: t('schedule.second'),
    3: t('schedule.third'),
    4: t('schedule.fourth'),
    5: t('schedule.fifth'),
    6: t('schedule.sixth'),
    7: t('schedule.seventh')
  };
  return periodNames[number] || number.toString();
};

const getBackendPeriodName = (number) => {
  const backendPeriodNames = {
    1: "الاولى",
    2: "الثانية",
    3: "الثالثة",
    4: "الرابعة",
    5: "الخامسة",
    6: "السادسة",
    7: "السابعة"
  };
  return backendPeriodNames[number] || number.toString();
};

const formatTime = (date) => {
  return date.toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit" });
};

const saturdayHasSchedulesIn = (list) => Array.isArray(list) && list.some((s) => s.day === "السبت");

const getPeriodsForDayIn = (list, day) => {
  if (day === "السبت" && !saturdayHasSchedulesIn(list)) {
    return [];
  }
  return periodsWithTimes.value;
};

const hasScheduleIn = (list, day, period) =>
  Array.isArray(list) && list.some((s) => s.day === day && s.period === period);

const getCellIn = (list, day, period) =>
  (Array.isArray(list) && list.find((s) => s.day === day && s.period === period)) || null;

const fetchChildrenSchedules = async () => {
  try {
    loading.value = true;
    const response = await api.get(`/guardian/schedule`);
    children.value = Array.isArray(response.data.data) ? response.data.data : [];
  } catch (error) {
    toast.error(error.response?.data?.message || t('schedule.load_failed'));
    children.value = [];
  } finally {
    loading.value = false;
  }
};

const printSchedule = () => {
  const wrapper = document.getElementById('schedule-to-print');
  const printContent = wrapper ? wrapper.innerHTML : '';
  const printWindow = window.open('', '_blank');

  printWindow.document.write(`
    <!DOCTYPE html>
    <html dir="rtl">
    <head>
      <title>${t('schedule.children_schedule_title')}</title>
      <meta charset="UTF-8">
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        h2 { text-align: center; margin-bottom: 20px; }

        .d-none.d-md-table { display: table !important; }
        .d-block.d-md-none { display: none !important; }

        table { width: 100%; border-collapse: collapse; margin: 0 auto 24px; table-layout: fixed; }
        th, td { padding: 8px; text-align: center; border: 1px solid #000; word-break: break-word; }
        thead th { font-weight: 700; }

        .bg-light { background-color: #f8f9fa !important; }
        .bg-secondary { background-color: #6c757d !important; color: white !important; }
        .text-muted { color: #6c757d !important; }

        .empty-day { background-color: #f8f9fa !important; color: #6c757d !important; font-style: italic; }

        @page { size: A4; margin: 16mm; }
        .print-footer { text-align: left; margin-top: 16px; font-size: 12px; }

        @media print {
          body { padding: 0; }
          .no-print { display: none !important; }
        }
      </style>
    </head>
    <body>
      <h2>${t('schedule.children_schedule_title')}</h2>
      ${printContent}
      <div class="print-footer">
        ${t('schedule.printed_at')} ${new Date().toLocaleString()}
      </div>
    </body>
    </html>
  `);

  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
};

onMounted(() => {
  fetchChildrenSchedules();
});
</script>

<style scoped>
.table-bordered td,
.table-bordered th {
  vertical-align: middle;
  height: 100px;
}

.empty-day {
  background-color: #f8f9fa !important;
  color: #6c757d !important;
  font-style: italic;
}

@media print {
  .no-print {
    display: none !important;
  }
}
</style>
